/**
 * Route guards.
 *
 * RequireAuth: when auth is enabled and no session exists, bounce to /login,
 * remembering where the user was headed. While the boot-time silent refresh is
 * still running it shows a spinner rather than flashing the login screen.
 *
 * RequireSysadmin: same, plus a 403-style redirect for non-sysadmins.
 */
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "./useAuth";

function Loading() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
      <CircularProgress />
    </Box>
  );
}

export function RequireAuth() {
  const { enabled, status } = useAuth();
  const location = useLocation();

  if (!enabled) return <Outlet />; // AUTH_MODE=none — nothing to guard
  if (status === "loading") return <Loading />;
  if (status !== "authenticated") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <Outlet />;
}

export function RequireSysadmin() {
  const { enabled, status, isSysadmin } = useAuth();

  if (!enabled) return <Outlet />;
  if (status === "loading") return <Loading />;
  if (status !== "authenticated") return <Navigate to="/login" replace />;
  if (!isSysadmin) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Redirect an already-authenticated user away from /login and /register. */
export function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { enabled, status } = useAuth();
  if (enabled && status === "authenticated") return <Navigate to="/" replace />;
  return <>{children}</>;
}
